import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, CheckCircle, Award, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const services = [
    {
      title: "Gradual Structured Learning",
      description: "Build your skills step-by-step with our proven teaching methodology",
      icon: <Users className="w-8 h-8" />
    },
    {
      title: "Intensive Learning",
      description: "Fast-track your learning with our intensive driving courses",
      icon: <Clock className="w-8 h-8" />
    },
    {
      title: "Refresher Lessons",
      description: "Get back on the road with confidence after a break from driving",
      icon: <CheckCircle className="w-8 h-8" />
    },
    {
      title: "Mock Tests",
      description: "Practice makes perfect - prepare for your test with realistic mock exams",
      icon: <Award className="w-8 h-8" />
    },
    {
      title: "Pass Plus",
      description: "Advance your skills beyond the test with our Pass Plus program",
      icon: <Award className="w-8 h-8" />
    },
    {
      title: "Driving Instructor Training",
      description: "Pay as you go training for aspiring driving instructors",
      icon: <Users className="w-8 h-8" />
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[600px] flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1611448746128-7c39e03b71e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxkcml2aW5nfGVufDB8fHx8MTc3NTY3MzQ0OXww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto px-4 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Maulana Zubair Divine<br />School of Motoring (DSM)
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Helping learners achieve safe driving for life in a safe and comfortable environment since 2002
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:07834618908">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg">
                <Phone className="mr-2 h-5 w-5" />
                Call: 07834 618908
              </Button>
            </a>
            <a href="mailto:zubairapabhai@gmail.com">
              <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20 px-8 py-6 text-lg">
                <Mail className="mr-2 h-5 w-5" />
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto text-center">
            <div className="p-6">
              <div className="text-5xl font-bold mb-2 text-emerald-400">1000+</div>
              <p className="text-lg text-slate-300">Pupils Passed Since 2002</p>
            </div>
            <div className="p-6">
              <div className="text-5xl font-bold mb-2 text-emerald-400">20+</div>
              <p className="text-lg text-slate-300">Years of Experience</p>
            </div>
            <div className="p-6">
              <div className="text-5xl font-bold mb-2 text-emerald-400">Very High</div>
              <p className="text-lg text-slate-300">Pass Rate</p>
            </div>
          </div>
          
          {/* Prominent Phone Number */}
          <div className="mt-12 text-center">
            <p className="text-slate-300 mb-3 text-lg">Book Your Lessons Today</p>
            <a href="tel:07834618908" className="inline-block">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-12 py-6 rounded-lg text-3xl md:text-4xl font-bold shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300 hover:scale-105">
                <Phone className="inline-block mr-3 h-10 w-10" />
                07834 618908
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-slate-900">Teaching Excellence Since 2002</h2>
            <div className="w-24 h-1 bg-emerald-600 mx-auto mb-8"></div>
            <p className="text-lg text-slate-700 mb-6 leading-relaxed">
              As an experienced driving instructor in Coventry for over two decades, my mission is to help learners achieve safe driving for life in a safe and comfortable environment. With over 1000 pupils successfully passing their tests, our proven track record speaks for itself.
            </p>
            <div className="grid md:grid-cols-2 gap-8 mt-12">
              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold mb-4 text-slate-900">Our Approach</h3>
                  <p className="text-slate-600">
                    We are efficient and effective, wasting no time at all. Our teaching methods are designed to maximize your learning while respecting your time and investment.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold mb-4 text-slate-900">Our Values</h3>
                  <p className="text-slate-600">
                    We are polite and honest, helping build your confidence and skills. Your success and safety are our top priorities in every lesson.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 text-slate-900">Our Services</h2>
            <div className="w-24 h-1 bg-emerald-600 mx-auto mb-6"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Competitively priced lessons tailored to your needs and learning style
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6 text-emerald-600">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-slate-900">{service.title}</h3>
                  <p className="text-slate-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Vehicles Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 text-slate-900">Our Premium Vehicles</h2>
            <div className="w-24 h-1 bg-emerald-600 mx-auto mb-6"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Learn in modern, premium dual-controlled vehicles for maximum comfort and safety
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <Card className="border-none shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1653321669786-f4b3f90cf235?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxBdWRpJTIwQTF8ZW58MHx8fHwxNzc1NzU0NDYzfDA&ixlib=rb-4.1.0&q=85" 
                  alt="Audi A1 S-line"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold mb-2 text-slate-900">Audi A1 S-line</h3>
                <p className="text-slate-600">Compact, stylish, and perfect for learning. Features dual controls and all modern safety features.</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1655288739707-4ea032b39a58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxDdXByYSUyMEZvcm1lbnRvcnxlbnwwfHx8fDE3NzU3NTQ0Njh8MA&ixlib=rb-4.1.0&q=85" 
                  alt="Cupra Formentor"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold mb-2 text-slate-900">Cupra Formentor</h3>
                <p className="text-slate-600">Premium SUV with excellent visibility and comfort. Fully equipped for comprehensive driver training.</p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-12">
            <p className="text-lg text-slate-600 mb-4">
              See more photos and videos of our vehicles and lessons on our social media
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://www.google.com/maps/search/Maulana+Zubair+divine+school+of+motoring+Coventry" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                  Google Reviews & Photos
                </Button>
              </a>
              <a href="https://www.tiktok.com/@divineschoolofmotoringmaulanazubair" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-slate-900 text-slate-900 hover:bg-slate-50">
                  TikTok Videos
                </Button>
              </a>
              <a href="https://www.youtube.com/@MaulanaZubairDivineSchoolOfMotoring" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                  YouTube Channel
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Driving Journey?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Get in touch today to book your lessons and take the first step towards driving independence
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:07834618908">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-slate-100 px-8 py-6 text-lg">
                <Phone className="mr-2 h-5 w-5" />
                07834 618908
              </Button>
            </a>
            <a href="mailto:zubairapabhai@gmail.com">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                <Mail className="mr-2 h-5 w-5" />
                zubairapabhai@gmail.com
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-slate-900">Based in Coventry</h2>
          <p className="text-lg text-slate-600">
            Serving the Coventry area with professional driving instruction since 2002
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
